/**
 * SHARING — the send side.
 *
 * The old implementation was `navigator.share({ text })` with a hand-built
 * string. That is a link with no payload: it lands in WhatsApp as a bare URL and
 * unfurls as nothing, because nothing on the other end ever served a preview.
 *
 * The two halves of the fix:
 *   - the backend now server-renders per-URL Open Graph tags and a real card
 *     image (see backend/src/share/), so a LINK finally unfurls;
 *   - and this file makes the phone share the IMAGE ITSELF where it can.
 *
 * That second half is the one that matters. `navigator.share({ files: [png] })`
 * is what makes the card arrive in the thread as a picture — visible in the
 * conversation, screenshot-able, forwardable — rather than as a grey link
 * rectangle somebody has to choose to tap. On desktop there is no share sheet
 * worth the name, so the share target is the clipboard, and we offer both the
 * image and the link.
 */

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '')
).replace(/\/$/, '');

/**
 * The public web origin — where shared links point. NOT the API origin: a link
 * to the Cloud Run URL would work but would show `wordrush-backend-391244…
 * .run.app` in the chat bubble, which looks like a phishing attempt and is the
 * fastest way to make a family not click.
 */
export const WEB_ORIGIN =
  (import.meta.env.VITE_PUBLIC_WEB_ORIGIN as string | undefined)?.replace(/\/$/, '') ||
  (typeof window !== 'undefined' && window.location.origin.includes('localhost')
    ? window.location.origin
    : 'https://wordrush.nookli.ai');

export interface ShareCardHero {
  kind: 'override' | 'collision' | 'invalid' | 'rare' | 'sweep' | 'scoreline';
  roundNumber: number | null;
  letter: string | null;
  category: string | null;
  answer: string | null;
  points: number | null;
  author: string | null;
  eyebrow: string | null;
  verdict: string | null;
  chipTone: 'ten' | 'five' | 'zero';
  names?: string[];
}

export interface ShareCard {
  table: string;
  date: string;
  hero: ShareCardHero;
  standings: { podium: Array<{ name: string; score: number }>; others: number };
  oneOfAKind: string | null;
  domain: string;
}

export interface ShareView {
  gameId: string;
  card: ShareCard;
  shareText: string;
  image: string;
  squareImage: string;
  url: string;
}

/** The card-safe view of a finished game. Public — no auth, by design. */
export async function fetchShareView(gameId: string): Promise<ShareView | null> {
  if (!API_BASE || !gameId) return null;
  try {
    const res = await fetch(`${API_BASE}/share/games/${encodeURIComponent(gameId)}`);
    if (!res.ok) return null;
    return (await res.json()) as ShareView;
  } catch {
    return null;
  }
}

/** One square per category. Never a word — see backend/src/share/dailyGrid.js. */
export interface DailyGridCell {
  category: string;
  status: 'unique' | 'duplicate' | 'blank' | 'invalid';
  points: number;
}

export interface DailyGrid {
  date: string;
  letter: string;
  cells: DailyGridCell[];
  total: number;
  max: number;
  /** Ready to paste into any chat on earth. */
  text: string;
  url: string;
  playedToday: number;
}

/**
 * Your daily grid. Requires auth — it is YOUR sheet, scored against everybody
 * else's. Returns null until you have actually submitted (a grid built from a
 * half-typed sheet would be a lie).
 */
export async function fetchDailyGrid(date: string): Promise<DailyGrid | null> {
  const { auth } = await import('./firebase');
  const user = auth?.currentUser;
  if (!user || !API_BASE || !date) return null;
  try {
    const token = await user.getIdToken();
    const res = await fetch(`${API_BASE}/daily/${encodeURIComponent(date)}/grid`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return (await res.json()) as DailyGrid;
  } catch {
    return null;
  }
}

export function gameShareUrl(gameId: string): string {
  return `${WEB_ORIGIN}/games/${encodeURIComponent(gameId)}`;
}

export function joinShareUrl(roomCode: string): string {
  return `${WEB_ORIGIN}/join/${encodeURIComponent(roomCode.toUpperCase())}`;
}

/** Can this browser put a FILE into the native share sheet? */
export function canShareFiles(): boolean {
  if (typeof navigator === 'undefined' || !navigator.canShare || !navigator.share) return false;
  try {
    // `canShare` must be probed with a real File — Safari returns true for an
    // empty descriptor and then throws at share() time.
    const probe = new File([new Blob([''], { type: 'image/png' })], 'probe.png', { type: 'image/png' });
    return navigator.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

export function canShareLink(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** Can we write an image to the clipboard? (Chrome/Edge yes, Firefox no.) */
export function canCopyImage(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard &&
    typeof (navigator.clipboard as Clipboard).write === 'function' &&
    typeof ClipboardItem !== 'undefined'
  );
}

async function fetchPng(url: string): Promise<Blob | null> {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.type.startsWith('image/') ? blob : null;
  } catch {
    return null;
  }
}

export type ShareOutcome = 'shared' | 'copied-image' | 'copied-link' | 'cancelled' | 'failed';

/**
 * Send the card.
 *
 * The order is deliberate and it is the whole product decision:
 *
 *   1. Share the IMAGE + the link (mobile, where the family actually is). The
 *      picture lands in the thread; the link rides along so the recipient can
 *      tap through to the growth payload.
 *   2. Share just the link (mobile browsers without file sharing — Firefox
 *      Android). Still unfurls, because the backend now serves real OG tags.
 *   3. Copy the link (desktop). The share target on a desktop IS the clipboard.
 *
 * A cancelled share is NOT a failure — the user tapped "cancel" and telling them
 * something went wrong would be a lie.
 */
export async function shareCard(view: ShareView, opts: { preferSquare?: boolean } = {}): Promise<ShareOutcome> {
  const imageUrl = opts.preferSquare === false ? view.image : view.squareImage;
  const text = view.shareText;
  const url = view.url;

  if (canShareFiles()) {
    const blob = await fetchPng(imageUrl);
    if (blob) {
      const file = new File([blob], 'wordrush.png', { type: 'image/png' });
      try {
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text, title: 'WordRush' });
          return 'shared';
        }
      } catch (e) {
        if ((e as DOMException)?.name === 'AbortError') return 'cancelled';
        // Fall through — a failed file share should still get the link out.
      }
    }
  }

  if (canShareLink()) {
    try {
      await navigator.share({ title: 'WordRush', text, url });
      return 'shared';
    } catch (e) {
      if ((e as DOMException)?.name === 'AbortError') return 'cancelled';
    }
  }

  return copyLink(view);
}

/** Desktop: put the PNG on the clipboard so it can be pasted straight into Slack. */
export async function copyImage(view: ShareView): Promise<ShareOutcome> {
  if (!canCopyImage()) return 'failed';
  try {
    // Safari requires the ClipboardItem to be constructed with a PROMISE of the
    // blob, inside the same user gesture — awaiting the fetch first and then
    // writing loses the gesture and the write is rejected.
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': fetchPng(view.squareImage).then(b => b || new Blob()) }),
    ]);
    return 'copied-image';
  } catch {
    // Chrome rejects a promise-based ClipboardItem in some versions; retry the
    // plain way now that we're (probably) still close enough to the gesture.
    try {
      const blob = await fetchPng(view.squareImage);
      if (!blob) return 'failed';
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
      return 'copied-image';
    } catch {
      return 'failed';
    }
  }
}

export async function copyLink(view: Pick<ShareView, 'url' | 'shareText'>): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(`${view.shareText}\n${view.url}`);
    return 'copied-link';
  } catch {
    return 'failed';
  }
}

export async function copyText(text: string): Promise<ShareOutcome> {
  try {
    await navigator.clipboard.writeText(text);
    return 'copied-link';
  } catch {
    return 'failed';
  }
}
